import React from "react";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Luxuriya Homes - Data Security & Trust</title>
        <meta 
          name="description" 
          content="Learn how Luxuriya Homes protects your personal data. We prioritize your privacy and ensure your information is used solely for delivering world-class interior designs." 
        />
        <meta 
          name="keywords" 
          content="Privacy Policy, Data Security, Luxuriya Homes Privacy, Interior Design Data Protection" 
        />
        <link rel="canonical" href="https://www.luxuriyahomes.com/privacy-policy" />
      </Helmet>

      <div className="bg-neutral-50 min-h-screen text-neutral-800 font-sans">
        
        {/* HERO / HEADER */}
        <header
          className="relative h-[50vh] md:h-[65vh] flex items-center justify-center text-white"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2000&auto=format&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/70"></div>

          {/* Content */}
          <div className="relative z-10 text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif tracking-widest text-[#D4AF37] mb-4">
              Privacy Policy
            </h1>

            <div className="w-24 md:w-32 h-[2px] bg-[#D4AF37] mx-auto mt-6 md:mt-8 mb-8 md:mb-10"></div>

            <p className="max-w-xl md:max-w-2xl mx-auto text-neutral-300 text-base md:text-lg leading-relaxed px-2">
              Aapka trust aur data security hamari priority hai. Yahan jaaniye ki
              hum aapki information kaise handle karte hain.
            </p>
          </div>
        </header>

        {/* CONTENT */}
        <main className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20">
          
          {/* INTRO */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
            <p className="text-base md:text-lg leading-loose text-neutral-600">
              At{" "}
              <span className="font-semibold text-black">
                Luxuriya Homes
              </span>, 
              hum aapki privacy ki respect karte hain. Hum ensure karte hain ki 
              aapka personal data safe rahe aur sirf aapko behtar interior design 
              services provide karne ke liye use ho.
            </p>
          </div>

          {/* SECTIONS */}
          <div className="space-y-8 md:space-y-12">
            <Section title="1. Information Collection">
              Service quality maintain karne ke liye hum kuch basic information collect karte hain:
              <ul className="list-disc pl-5 md:pl-6 space-y-2 mt-4">
                 <li><strong>Personal Details:</strong> Name, Email, Phone Number aur Address.</li>
                 <li><strong>Project Details:</strong> Floor plans, site photos, aur design preferences.</li>
                 <li><strong>Technical Data:</strong> Cookies aur IP address website experience improve karne ke liye.</li>
              </ul>
            </Section>

            <Section title="2. Use of Information">
              Aapka data sirf niche diye gaye purposes ke liye use kiya jata hai:
              <ul className="list-disc pl-5 md:pl-6 space-y-2 mt-4">
                <li>Aapke project ki planning aur execution ke liye.</li>
                <li>Aapko design updates aur quotations bhejne ke liye.</li>
                <li>Customer support aur queries resolve karne ke liye.</li>
                <li>Hum aapka data kisi bhi irrelevant third-party ko sell nahi karte.</li>
              </ul>
            </Section>

            <Section title="3. Data Sharing & Disclosure">
              Hum aapki information strictly confidential rakhte hain. Isse share sirf tab kiya jata hai jab:
              <ul className="list-disc pl-5 md:pl-6 space-y-2 mt-4">
                <li>Project execution ke liye trusted vendors/contractors ki zaroorat ho.</li>
                <li>Legal compliance ya government authorities dwara required ho.</li>
                <li>Payment processing ke liye secure gateways use kiye ja rahe hon.</li>
              </ul>
            </Section>

            <Section title="4. Data Security">
              Aapke personal data ko unauthorized access, alteration ya leakage se bachane ke liye hum industry-standard security measures aur secure servers use karte hain.
            </Section>

            <Section title="5. Your Rights">
              Aap kisi bhi waqt apni information review karne, update karne ya delete karwane ki request kar sakte hain. Agar aap marketing communications receive nahi karna chahte, to aap easily opt-out kar sakte hain.
            </Section>
          </div>

          {/* FOOTER NOTE */}
          <div className="mt-16 md:mt-24 pt-10 border-t border-neutral-200 text-center text-sm text-neutral-500">
            <p>Last Updated: January 2026</p>
            <p className="mt-3">
              Privacy related queries ke liye contact karein:{" "}
              <a
                href="mailto:privacy@luxuriyahomes.com"
                className="text-[#D4AF37] hover:underline"
              >
                privacy@luxuriyahomes.com
              </a>
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

/* SECTION COMPONENT */
type SectionProps = {
  title: string;
  children: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <section className="bg-white rounded-xl p-6 md:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.06)] border-l-4 border-[#D4AF37]">
    <h2 className="text-xl md:text-3xl font-serif text-black mb-4 md:mb-5">
      {title}
    </h2>
    <div className="text-neutral-600 leading-relaxed text-sm md:text-[15px]">
      {children}
    </div>
  </section>
);

export default PrivacyPolicy;